/**
 * TrendsPage.jsx — Platform trend monitor (WP5, docs/har-corpus-spec.md §8).
 *
 * Executive view: one column per platform (iOS/Android/…), each showing a
 * handful of headline metrics (CPM p50, bid rate, top buyer fill, EIDs
 * presence, consent presence) as a big current value + a delta chip vs the
 * cohort baseline + a 14-point sparkline, plus a vendor-stack chip row, a
 * platform pass/watch/off status chip, and the session-level advisories
 * (§6). A divergence banner surfaces when the platform_divergence detector
 * (§5.3) is live between the two platforms. The current LIVE session (if
 * one is connected) is compared against the corpus and rendered as a
 * visually distinct newest point on top of the historical series.
 *
 * Data sources (both provided by another agent's concurrent work on
 * proxy-server/apiServer.js — this page never assumes they exist):
 *   GET  /proxy/corpus/scorecard?app=<id>  → per-app-platform cohort history
 *   POST /proxy/corpus/compare             → this session vs its cohort
 * (api/corpus.js). Every field access below is defensive — the backend
 * routes' exact response shape isn't locked down yet, so this page
 * tolerates missing/differently-shaped data and degrades to "no data"
 * rather than crashing (see normalizeScorecard() below for the specific
 * shapes tolerated).
 *
 * No charting library exists in this repo (verified via package.json) — the
 * sparkline is a small hand-rolled inline SVG, same convention as
 * StreamActivityMonitor.jsx's bar sparkline elsewhere in the dashboard.
 */
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useTraffic } from '../context/TrafficContext';
import { getAllApps, getActiveSelection } from '../config/appProfiles';
import { platformOf } from '../validation/bidHealth';
import { getScorecard, compareSession } from '../api/corpus';
import { buildCorpusProjection } from '../utils/corpusProjection';
import Sparkline from '../components/Sparkline';

// ── tunables ─────────────────────────────────────────────────────────────

// Reuses the spec's own §5.1 "cpm.n ≥ 50" sample gate as the general
// "trust this number" floor for tile/point rendering — a metric with fewer
// samples than this renders as a gated/"thin sample" point rather than a
// trend signal, per the spec's reliability rules (§8).
const MIN_SAMPLE = 50;

// Per-placement sample floor — mirrors the proxy detector's
// PLACEMENT_SELF_TREND_MIN_N (trendDetectors.js): a single placement's bid
// sample is a slice of the session, so the session-wide MIN_SAMPLE (50)
// would leave nearly every placement permanently "thin".
const PLACEMENT_MIN_SAMPLE = 20;

// Placements shown per platform column (ranked by sample size).
const MAX_PLACEMENT_ROWS = 6;

// A live session needs at least this many captured rows before we bother
// POSTing a comparison — avoids spamming /proxy/corpus/compare with a
// near-empty projection the instant the page loads.
const MIN_LIVE_ROWS_FOR_COMPARE = 5;

// Debounce between live-row changes and firing compareSession() — live
// capture can add rows many times a second; this keeps the compare call to
// roughly once every few seconds rather than on every single WS event.
const COMPARE_DEBOUNCE_MS = 4000;

const METRIC_TILES = [
  // "all bids" = every responded Prebid bid's price (won AND losing) — the
  // bid landscape. The winning-side number is the win bucket below (hb_pb,
  // the winner's price-bucketed CPM Prebid forwarded into the GAM request).
  { id: 'cpm', label: 'CPM (p50, all bids)', metricPath: 'cpm.p50', nPath: 'cpm.n', format: fmtMoney },
  { id: 'winBucket', label: 'Win CPM (hb_pb p50)', metricPath: 'winBucket.p50', nPath: 'winBucket.n', format: fmtMoney },
  { id: 'bidRate', label: 'Bid Rate', metricPath: 'bidRate.rate', nPath: 'bidRate.n', format: fmtPct },
  { id: 'topBuyerFill', label: 'Top Buyer Fill', special: 'buyerFill', format: fmtPct },
  { id: 'eidsPresence', label: 'EIDs Presence', metricPath: 'eidsPresence.total.share', nPath: 'eidsPresence.total.n', format: fmtPct },
  { id: 'consentPresence', label: 'Consent Presence', metricPath: 'consentPresence.share', nPath: 'consentPresence.n', format: fmtPct },
];

const STATUS_COLOR = { pass: 'success', watch: 'warning', off: 'default' };
const STATUS_LABEL = { pass: 'Pass', watch: 'Watch', off: 'Off' };

// ── formatting helpers ──────────────────────────────────────────────────

function fmtMoney(v) {
  return typeof v === 'number' && Number.isFinite(v) ? `$${v.toFixed(2)}` : '—';
}
function fmtPct(v) {
  return typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v * 100)}%` : '—';
}

// ── generic defensive-access helpers ────────────────────────────────────

function getAt(obj, path) {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((cur, k) => (cur && typeof cur === 'object' ? cur[k] : undefined), obj);
}

function medianOf(vals) {
  const nums = (vals || []).filter((v) => typeof v === 'number' && Number.isFinite(v)).slice().sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2;
}

function dedupeById(items) {
  const seen = new Set();
  const out = [];
  for (const it of items || []) {
    const id = it && it.id;
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    out.push(it);
  }
  return out;
}

function normalizePlatform(pRaw) {
  const p = String(pRaw || '').toLowerCase();
  if (p === 'ios' || p === 'android') return p;
  return 'unknown';
}

// ── scorecard normalization — the backend's exact response shape is being
// built concurrently and isn't locked down here, so this tolerates every
// shape the spec's prose ("cohorts: per app×platform {...}") plausibly
// means: cohorts keyed directly by platform, cohorts keyed by app then
// platform, or cohorts as a flat array of per-cohort records. ─────────────

function normalizeScorecard(data, appId) {
  if (!data || typeof data !== 'object') return [];
  const cohorts = data.cohorts;
  if (!cohorts) return [];

  const out = [];
  if (Array.isArray(cohorts)) {
    for (const item of cohorts) {
      if (!item || typeof item !== 'object') continue;
      if (item.app != null && appId != null && item.app !== appId) continue;
      out.push({ ...item, platform: normalizePlatform(item.platform || item.platformId) });
    }
    return out;
  }

  if (typeof cohorts === 'object') {
    for (const [key, value] of Object.entries(cohorts)) {
      if (!value || typeof value !== 'object') continue;
      const looksLikeCohort = value.metrics || value.series || value.alerts || value.advisories;
      if (looksLikeCohort) {
        out.push({ ...value, platform: normalizePlatform(key) });
      } else {
        // key is probably an app id — descend one more level for its platforms
        for (const [pKey, pVal] of Object.entries(value)) {
          if (!pVal || typeof pVal !== 'object') continue;
          out.push({ ...pVal, platform: normalizePlatform(pKey) });
        }
      }
    }
  }
  return out;
}

function tsOf(pt) {
  const v = pt && (pt.capturedAt || pt.ts || pt.timestamp);
  const t = v ? Date.parse(v) : NaN;
  return Number.isFinite(t) ? t : 0;
}

// Last 14, chronological (oldest → newest) — defensive re-sort since the
// backend's own ordering isn't guaranteed here.
function normalizeSeries(series) {
  const arr = (Array.isArray(series) ? series : []).filter((pt) => pt && typeof pt === 'object');
  arr.sort((a, b) => tsOf(a) - tsOf(b));
  return arr.slice(-14);
}

// A series point may itself carry a `metrics` sub-object (a slim per-session
// snapshot, mirroring a cohort record) or may already BE a flat metrics
// snapshot — try both.
function pointValue(pt, path) {
  const nested = getAt(pt, `metrics.${path}`);
  if (nested != null) return nested;
  return getAt(pt, path);
}
function pointN(pt, nPath) {
  const nested = getAt(pt, `metrics.${nPath}`);
  if (nested != null) return nested;
  return getAt(pt, nPath);
}
function isGated(pt, n) {
  if (pt && (pt.gated === true || pt.thin === true)) return true;
  return !(typeof n === 'number' && Number.isFinite(n) && n >= MIN_SAMPLE);
}

function topBuyer(metrics) {
  const buyerFill = metrics && metrics.buyerFill;
  if (!buyerFill || typeof buyerFill !== 'object') return null;
  let best = null;
  for (const [name, info] of Object.entries(buyerFill)) {
    if (!info) continue;
    const n = typeof info.n === 'number' ? info.n : (typeof info.bidCount === 'number' ? info.bidCount : 0);
    if (!best || n > best.n) best = { name, info, n };
  }
  return best;
}
function topBuyerFillValue(metrics) {
  const b = topBuyer(metrics);
  return b && typeof b.info.fillRate === 'number' ? b.info.fillRate : null;
}
function topBuyerFillN(metrics) {
  const b = topBuyer(metrics);
  return b ? b.n : null;
}
function topBuyerFillLabel(metrics) {
  const b = topBuyer(metrics);
  return b ? `${b.name} fill` : 'Top buyer fill';
}

// ── small presentational subcomponents ──────────────────────────────────
//
// Sparkline itself now lives in components/Sparkline.jsx (SpendIndicator.jsx
// is its second caller) — imported above, behavior unchanged for both call
// sites in this file.

function DeltaChip({ current, baseline, n, alert, minSample = MIN_SAMPLE }) {
  const thin = !(typeof n === 'number' && Number.isFinite(n) && n >= minSample);
  if (thin) {
    return <Chip size="small" label="thin sample" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'text.disabled' }} />;
  }
  if (typeof current !== 'number' || typeof baseline !== 'number' || baseline === 0) {
    return <Chip size="small" label="no baseline yet" variant="outlined" sx={{ color: 'text.disabled' }} />;
  }
  const delta = (current - baseline) / baseline;
  if (!Number.isFinite(delta)) return <Chip size="small" label="—" variant="outlined" />;
  const arrow = delta >= 0 ? '▲' : '▼';
  const label = `${arrow} ${Math.abs(Math.round(delta * 100))}%`;
  const color = alert ? (alert.severity === 'fail' ? 'error' : 'warning') : 'default';
  return <Chip size="small" label={label} color={color} variant={alert ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />;
}

function MetricTile({ def, cohortMetrics, series, liveMetrics, alerts, platform }) {
  const isBuyer = def.special === 'buyerFill';

  const liveVal = liveMetrics ? (isBuyer ? topBuyerFillValue(liveMetrics) : getAt(liveMetrics, def.metricPath)) : null;
  const liveN = liveMetrics ? (isBuyer ? topBuyerFillN(liveMetrics) : getAt(liveMetrics, def.nPath)) : null;

  const lastPt = series.length ? series[series.length - 1] : null;
  const lastVal = lastPt ? (isBuyer ? topBuyerFillValue(getAt(lastPt, 'metrics') || lastPt) : pointValue(lastPt, def.metricPath)) : null;
  const lastN = lastPt ? (isBuyer ? topBuyerFillN(getAt(lastPt, 'metrics') || lastPt) : pointN(lastPt, def.nPath)) : null;

  const aggVal = isBuyer ? topBuyerFillValue(cohortMetrics) : getAt(cohortMetrics, def.metricPath);
  const aggN = isBuyer ? topBuyerFillN(cohortMetrics) : getAt(cohortMetrics, def.nPath);

  const current = liveVal != null ? liveVal : (lastVal != null ? lastVal : aggVal);
  const n = liveVal != null ? liveN : (lastVal != null ? lastN : aggN);

  const seriesPoints = series.map((pt) => {
    const m = getAt(pt, 'metrics') || pt;
    const v = isBuyer ? topBuyerFillValue(m) : pointValue(pt, def.metricPath);
    const pn = isBuyer ? topBuyerFillN(m) : pointN(pt, def.nPath);
    return { value: v, gated: isGated(pt, pn) };
  });
  if (liveMetrics) {
    seriesPoints.push({ value: liveVal, gated: !(typeof liveN === 'number' && liveN >= MIN_SAMPLE), isCurrent: true });
  }

  let baseline = medianOf(series.map((pt) => {
    const m = getAt(pt, 'metrics') || pt;
    return isBuyer ? topBuyerFillValue(m) : pointValue(pt, def.metricPath);
  }));

  const matchingAlert = (alerts || []).find((a) => a
    && (a.kind === 'cohort_self_trend' || a.kind === 'platform_divergence')
    && a.metric === def.metricPath
    && String(a.subject || '').toLowerCase().includes(platform));
  if (matchingAlert && matchingAlert.baseline) {
    const bv = typeof matchingAlert.baseline.value === 'number'
      ? matchingAlert.baseline.value
      : (typeof matchingAlert.baseline[platform] === 'number' ? matchingAlert.baseline[platform] : null);
    if (bv != null) baseline = bv;
  }

  const label = isBuyer ? topBuyerFillLabel(liveMetrics || cohortMetrics) : def.label;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <DeltaChip current={current} baseline={baseline} n={n} alert={matchingAlert} />
      </Stack>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.25 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, minWidth: 64 }}>
          {typeof current === 'number' ? def.format(current) : '—'}
        </Typography>
        <Box sx={{ color: matchingAlert ? (matchingAlert.severity === 'fail' ? 'error.main' : 'warning.main') : 'text.secondary' }}>
          <Sparkline points={seriesPoints} baselineValue={baseline} />
        </Box>
      </Stack>
    </Box>
  );
}

// ── per-placement CPM breakdown ─────────────────────────────────────────
//
// CPMs vary per placement, so a platform-wide p50 can hide one ad unit
// collapsing while another props the median up. The proxy's corpus metrics
// carry a `placements` map (keyed by GAM iu path / Prebid GPID, capped
// server-side); the scorecard summary exposes `placements` with
// current/baseline/delta, and each series point carries a slim per-placement
// {cpm: {p50, n}}. Placement keys can contain dots, so everything here
// indexes maps directly — never dotted getAt paths.

function placementLabel(key) {
  const s = String(key);
  const parts = s.split('/').filter(Boolean);
  if (parts.length > 2) return `…/${parts.slice(-2).join('/')}`;
  return s;
}

function placementPoint(pt, key) {
  const m = getAt(pt, 'metrics') || pt;
  const p = m && m.placements && typeof m.placements === 'object' ? m.placements[key] : null;
  const value = p && p.cpm && typeof p.cpm.p50 === 'number' && Number.isFinite(p.cpm.p50) ? p.cpm.p50 : null;
  const n = p && p.cpm && typeof p.cpm.n === 'number' ? p.cpm.n : null;
  return { value, n };
}

function PlacementsSection({ cohortPlacements, series, liveMetrics, alerts, platform }) {
  const livePlacements = (liveMetrics && liveMetrics.placements && typeof liveMetrics.placements === 'object')
    ? liveMetrics.placements : {};
  const summary = (cohortPlacements && typeof cohortPlacements === 'object') ? cohortPlacements : {};
  const keys = [...new Set([...Object.keys(livePlacements), ...Object.keys(summary)])];
  if (!keys.length) return null;

  const rows = keys.map((key) => {
    const live = livePlacements[key];
    const sum = summary[key];
    const liveVal = live && live.cpm && Number.isFinite(live.cpm.p50) ? live.cpm.p50 : null;
    const liveN = live && live.cpm && Number.isFinite(live.cpm.n) ? live.cpm.n : null;

    const current = liveVal != null ? liveVal : (sum && sum.current != null ? sum.current : null);
    const n = liveVal != null ? liveN : (sum && sum.currentN != null ? sum.currentN : null);
    const baseline = sum && sum.baseline != null
      ? sum.baseline
      : medianOf(series.map((pt) => placementPoint(pt, key).value));

    const seriesPoints = series.map((pt) => {
      const p = placementPoint(pt, key);
      return { value: p.value, gated: !(typeof p.n === 'number' && p.n >= PLACEMENT_MIN_SAMPLE) };
    });
    if (liveMetrics) {
      seriesPoints.push({ value: liveVal, gated: !(typeof liveN === 'number' && liveN >= PLACEMENT_MIN_SAMPLE), isCurrent: true });
    }

    const alert = (alerts || []).find((a) => a
      && a.kind === 'cohort_self_trend'
      && a.metric === `placements.${key}.cpm.p50`
      && String(a.subject || '').toLowerCase().includes(platform));

    // Winning-side CPM for this placement — hb_pb win bucket (only present
    // on GAM requests where Prebid actually won and forwarded targeting).
    const liveWin = live && live.winBucket && Number.isFinite(live.winBucket.p50) && live.winBucket.n > 0
      ? live.winBucket.p50 : null;
    const sumWin = sum && sum.winBucket && Number.isFinite(sum.winBucket.current) && sum.winBucket.n > 0
      ? sum.winBucket.current : null;
    const win = liveWin != null ? liveWin : sumWin;

    const sortN = Math.max(liveN || 0, (sum && sum.currentN) || 0);
    return { key, current, n, baseline, seriesPoints, alert, win, sortN };
  })
    .sort((a, b) => b.sortN - a.sortN)
    .slice(0, MAX_PLACEMENT_ROWS);

  return (
    <Box sx={{ mt: 1.75 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        Placements — CPM (p50, all bids)
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {rows.map((r) => (
          <Box key={r.key}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="caption" title={r.key} sx={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {placementLabel(r.key)}
              </Typography>
              <DeltaChip current={r.current} baseline={r.baseline} n={r.n} alert={r.alert} minSample={PLACEMENT_MIN_SAMPLE} />
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 52 }}>
                {typeof r.current === 'number' ? fmtMoney(r.current) : '—'}
              </Typography>
              {typeof r.win === 'number' && (
                <Tooltip title="Winning CPM — hb_pb price bucket forwarded to GAM">
                  <Typography variant="caption" color="text.secondary">win {fmtMoney(r.win)}</Typography>
                </Tooltip>
              )}
              <Box sx={{ color: r.alert ? (r.alert.severity === 'fail' ? 'error.main' : 'warning.main') : 'text.secondary' }}>
                <Sparkline points={r.seriesPoints} baselineValue={r.baseline} width={110} height={26} />
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function VendorStackRow({ vendors, dropoutAlerts }) {
  const entries = Object.entries(vendors || {})
    .sort((a, b) => (b[1]?.requests || 0) - (a[1]?.requests || 0))
    .slice(0, 8);
  if (!entries.length) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
      {entries.map(([vendor, info]) => {
        const alert = (dropoutAlerts || []).find((a) => a && a.metric === `vendors.${vendor}`);
        let color = 'success';
        let variant = 'filled';
        let label = `${vendor} ${fmtPct(info && info.share)}`;
        if (alert) {
          if (alert.severity === 'fail') { color = 'error'; variant = 'outlined'; label = `${vendor} dropout`; }
          else { color = 'warning'; variant = 'filled'; label = `${vendor} reduced`; }
        }
        return <Chip key={vendor} size="small" color={color} variant={variant} label={label} />;
      })}
    </Stack>
  );
}

function DivergenceBanner({ alert }) {
  if (!alert) return null;
  return (
    <Alert severity={alert.severity === 'fail' ? 'error' : 'warning'} sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{alert.summary}</Typography>
      {Array.isArray(alert.nextStep) && alert.nextStep.slice(0, 3).map((s, i) => (
        <Typography key={i} variant="caption" display="block" sx={{ mt: 0.5 }}>• {s}</Typography>
      ))}
    </Alert>
  );
}

function AdvisoriesList({ advisories, title }) {
  if (!Array.isArray(advisories) || !advisories.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{title}</Typography>
      <Stack spacing={0.75} sx={{ mt: 0.5 }}>
        {advisories.map((a, i) => {
          const suppressed = !!(a && a.suppressedBy);
          return (
            <Box key={(a && a.id) || i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', opacity: suppressed ? 0.55 : 1 }}>
              <Chip
                size="small"
                label={a && a.severity === 'warn' ? '!' : 'i'}
                color={a && a.severity === 'warn' ? 'warning' : 'info'}
                sx={{ minWidth: 22, height: 20, '& .MuiChip-label': { px: 0.75 } }}
              />
              <Typography variant="caption" sx={{ flex: 1 }}>
                {a && a.summary}
                {suppressed && (
                  <Chip size="small" label="suppressed: test session" variant="outlined" sx={{ ml: 1, height: 18 }} />
                )}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function computeStatus(cohort, compare) {
  const hasData = !!(cohort && cohort.metrics) || !!(compare && compare.data && compare.data.metrics);
  if (!hasData) return 'off';
  const alerts = [...((cohort && cohort.alerts) || []), ...((compare && compare.data && compare.data.alerts) || [])];
  return alerts.length ? 'watch' : 'pass';
}

function PlatformColumn({ platform, cohort, compare }) {
  const metrics = (cohort && cohort.metrics) || null;
  const series = normalizeSeries(cohort && cohort.series);
  const cohortAlerts = Array.isArray(cohort && cohort.alerts) ? cohort.alerts : [];
  const cohortAdvisories = Array.isArray(cohort && cohort.advisories) ? cohort.advisories : [];

  const liveMetrics = (compare && compare.data && compare.data.metrics) || null;
  const liveAlerts = Array.isArray(compare && compare.data && compare.data.alerts) ? compare.data.alerts : [];
  const liveAdvisories = Array.isArray(compare && compare.data && compare.data.advisories) ? compare.data.advisories : [];

  const allAlerts = [...cohortAlerts, ...liveAlerts];
  const dropoutAlerts = allAlerts.filter((a) => a && a.kind === 'vendor_dropout');
  const status = computeStatus(cohort, compare);

  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 320px', minWidth: 300 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>{platform}</Typography>
        <Chip size="small" label={STATUS_LABEL[status]} color={STATUS_COLOR[status]} variant={status === 'off' ? 'outlined' : 'filled'} />
      </Stack>

      {!metrics && !liveMetrics && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
          No corpus history yet for this platform.
        </Typography>
      )}

      <Stack spacing={1.75} sx={{ mt: 1.5 }}>
        {METRIC_TILES.map((def) => (
          <MetricTile
            key={def.id}
            def={def}
            cohortMetrics={metrics}
            series={series}
            liveMetrics={liveMetrics}
            alerts={allAlerts}
            platform={platform}
          />
        ))}
      </Stack>

      <PlacementsSection
        cohortPlacements={cohort && cohort.placements}
        series={series}
        liveMetrics={liveMetrics}
        alerts={allAlerts}
        platform={platform}
      />

      <VendorStackRow vendors={(liveMetrics || metrics) && (liveMetrics || metrics).vendors} dropoutAlerts={dropoutAlerts} />

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.disabled">Baseline: trailing 7d or last 5 sessions</Typography>

      <AdvisoriesList advisories={cohortAdvisories} title="Advisories (corpus)" />
      <AdvisoriesList advisories={liveAdvisories} title="This session" />

      {compare && compare.loading && (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.disabled">comparing current session…</Typography>
        </Stack>
      )}
      {compare && compare.truncated && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
          Live session projection was truncated to fit the size cap.
        </Typography>
      )}
      {compare && compare.error && (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 1 }}>
          Couldn't compare the current session: {compare.error.message}
        </Typography>
      )}
    </Paper>
  );
}

// ── page ─────────────────────────────────────────────────────────────────

export default function TrendsPage() {
  const traffic = useTraffic() || {};
  const { liveTrafficGroups } = traffic;

  const apps = useMemo(() => getAllApps(), []);
  const [appId, setAppId] = useState(() => getActiveSelection().appId);
  const [scorecardState, setScorecardState] = useState({ loading: true, data: undefined, error: null });
  const [compareByPlatform, setCompareByPlatform] = useState({});
  // Manual refetch trigger for the corpus history. The LIVE overlay already
  // auto-refreshes (debounced compare on new WS rows below); the historical
  // scorecard only changes when a session is extracted, so a button beats
  // polling.
  const [refreshKey, setRefreshKey] = useState(0);

  // GET /proxy/corpus/scorecard?app=<id> — data === null means the route
  // isn't live yet (404); data === {cohorts:{}} (or similar empty) means the
  // route works but this app has no corpus history yet (cold start); either
  // way this never throws into the render — see api/corpus.js.
  useEffect(() => {
    let cancelled = false;
    setScorecardState({ loading: true, data: undefined, error: null });
    getScorecard(appId)
      .then((data) => { if (!cancelled) setScorecardState({ loading: false, data, error: null }); })
      .catch((error) => { if (!cancelled) setScorecardState({ loading: false, data: undefined, error }); });
    return () => { cancelled = true; };
  }, [appId, refreshKey]);

  // Live rows grouped by normalized platform (ios/android/unknown) — reuses
  // the dashboard's own platformOf() heuristic (bidHealth.js) rather than
  // re-deriving platform detection here.
  const liveRowsByPlatform = useMemo(() => {
    const rows = Object.values(liveTrafficGroups || {}).flat();
    const byPlat = {};
    for (const r of rows) {
      const p = normalizePlatform(platformOf(r));
      if (!byPlat[p]) byPlat[p] = [];
      byPlat[p].push(r);
    }
    return byPlat;
  }, [liveTrafficGroups]);

  const liveRowCountsKey = Object.entries(liveRowsByPlatform).map(([p, rows]) => `${p}:${rows.length}`).join(',');

  // Debounced POST /proxy/corpus/compare per platform once it has enough
  // rows to be worth projecting. A 404 (route not live) resolves to `data:
  // null` rather than an error — rendered as "no live comparison available"
  // rather than a scary failure.
  useEffect(() => {
    const platforms = Object.keys(liveRowsByPlatform);
    if (!platforms.length) return undefined;
    const timer = setTimeout(() => {
      platforms.forEach((platform) => {
        const rows = liveRowsByPlatform[platform];
        if (!rows || rows.length < MIN_LIVE_ROWS_FOR_COMPARE) return;
        const { rows: projected, truncated } = buildCorpusProjection(rows);
        setCompareByPlatform((prev) => ({ ...prev, [platform]: { ...(prev[platform] || {}), loading: true, error: null } }));
        compareSession({ app: appId, platform, rows: projected })
          .then((data) => {
            setCompareByPlatform((prev) => ({ ...prev, [platform]: { loading: false, data, truncated, error: null } }));
          })
          .catch((error) => {
            setCompareByPlatform((prev) => ({ ...prev, [platform]: { loading: false, data: null, truncated, error } }));
          });
      });
    }, COMPARE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, liveRowCountsKey]);

  const cohortsList = useMemo(() => normalizeScorecard(scorecardState.data, appId), [scorecardState.data, appId]);

  const platforms = useMemo(() => {
    const set = new Set([...cohortsList.map((c) => c.platform), ...Object.keys(liveRowsByPlatform)]);
    const order = ['ios', 'android'];
    return [...set].sort((a, b) => {
      const ai = order.indexOf(a);
      const bi = order.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [cohortsList, liveRowsByPlatform]);

  const divergenceAlerts = useMemo(() => {
    const all = [
      ...cohortsList.flatMap((c) => c.alerts || []),
      ...Object.values(compareByPlatform).flatMap((c) => (c && c.data && c.data.alerts) || []),
    ];
    return dedupeById(all.filter((a) => a && a.kind === 'platform_divergence'));
  }, [cohortsList, compareByPlatform]);

  const routeMissing = scorecardState.data === null && !scorecardState.loading && !scorecardState.error;
  const coldStart = !routeMissing && !scorecardState.loading && !scorecardState.error && platforms.length === 0;
  const noLiveSession = Object.keys(liveRowsByPlatform).length === 0;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Trends</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform trend monitor — corpus baselines vs the live session.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="trends-app-label">App</InputLabel>
            <Select labelId="trends-app-label" label="App" value={appId} onChange={(e) => setAppId(e.target.value)}>
              {Object.entries(apps).map(([id, app]) => (
                <MenuItem key={id} value={id}>{(app && app.name) || id}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Reload corpus history (the live-session overlay refreshes automatically)">
            <span>
              <IconButton size="small" onClick={() => setRefreshKey((k) => k + 1)} disabled={scorecardState.loading} aria-label="refresh corpus history">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {scorecardState.loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ my: 4 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading corpus scorecard…</Typography>
        </Stack>
      )}

      {routeMissing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Corpus trend monitor isn't available on this proxy yet — the /proxy/corpus routes aren't live. This page will
          populate automatically once they are.
        </Alert>
      )}

      {scorecardState.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn't load the corpus scorecard: {scorecardState.error.message}
        </Alert>
      )}

      {coldStart && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No corpus data yet for {(apps[appId] && apps[appId].name) || appId} — capture and save a few sessions to
          start building trend history.
        </Alert>
      )}

      {divergenceAlerts.map((a) => <DivergenceBanner key={a.id} alert={a} />)}

      {platforms.length > 0 && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {platforms.map((platform) => (
            <PlatformColumn
              key={platform}
              platform={platform}
              cohort={cohortsList.find((c) => c.platform === platform) || null}
              compare={compareByPlatform[platform] || null}
            />
          ))}
        </Stack>
      )}

      {platforms.length > 0 && noLiveSession && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
          No live session connected — showing historical corpus data only.
        </Typography>
      )}
    </Box>
  );
}
